import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  LiaoChartData,
  LoadingState,
  ParameterLimitsValues
} from '../../types';
import { message } from 'antd';
import { WorkerMessage, WorkerResponse } from './liao.worker';
import { computeIsLiaoDisabled } from './liaoUtils';
import { useAppContext } from '../../../../../../providers/AppContext';
import { useSelected } from '../../../../../../providers/selectedContext';
import { CalculateSamplePayload, Sample } from '../../../../../../@types/samples/types';
import { calculateSample } from '../../../../../../api/data';
import {
  buildLambdaSampleCurve,
  buildLiaoSampleCurve,
  clampCurvePointCount,
  DEFAULT_SAMPLE_CURVE_POINTS
} from '../../../../../utils/curveGeneration';

const INITIAL_LOADING_STATE: LoadingState = {
  custom: false,
  bestValues: false
};
const INITIAL_CUSTOM_VALUES: ParameterLimitsValues = {
  a: 0,
  b: 0,
  pce: 0,
  largeDerivative: 0
};
const INITIAL_CHART_DATA: LiaoChartData = {
  calculated: [],
  original: [],
  lambda: []
};

const hasValidCustomLiaoValues = (values: ParameterLimitsValues) =>
  Number.isFinite(values.a) &&
  Number.isFinite(values.b) &&
  Number.isFinite(values.pce) &&
  Number.isFinite(values.largeDerivative) &&
  values.a > 0 &&
  values.b >= 1 &&
  values.pce >= 0 &&
  values.largeDerivative < 0;

export function useLiaoAnalysis() {
  const { data, updateData } = useAppContext();
  const { selectedInfo, setSelectedInfo } = useSelected();

  const workerRef = useRef<Worker>();
  const [workerReady, setWorkerReady] = useState(false);
  const [isLocalCalculated, setIsLocalCalculated] = useState(false);
  const [loading, setLoading] = useState(INITIAL_LOADING_STATE);
  const [customValues, setCustomValues] = useState(INITIAL_CUSTOM_VALUES);
  const [chartData, setChartData] = useState(INITIAL_CHART_DATA);
  const [numberOfPoints, setNumberOfPoints] = useState(DEFAULT_SAMPLE_CURVE_POINTS);

  const originalSelectedSample = useMemo(
    () =>
      data.wellSamples.find(sample => sample.uid === selectedInfo.sample?.uid),
    [selectedInfo.sample?.uid, data.wellSamples]
  );

  const isCalculateLiaoAnalysisDisabled = useMemo(
    () => computeIsLiaoDisabled(selectedInfo.sample, loading),
    [loading, selectedInfo.sample]
  );

  const calculateInWorker = useCallback(
    async (input: WorkerMessage): Promise<Sample> => {
      const worker = workerRef.current;
      if (!worker) {
        return Promise.reject(new Error('Worker not initialized'));
      }

      return new Promise((resolve, reject) => {
        const handleMessage = (e: MessageEvent<WorkerResponse>) => {
          const data = e.data as WorkerResponse & { __error__?: true; message?: string };
          if (data?.__error__) {
            reject(new Error(data.message ?? 'Error trying to calculate liao'));
            cleanup();
            return;
          }

          resolve(data);
          cleanup();
        };

        const handleError = (e: ErrorEvent) => {
          reject(new Error(e.message));
          cleanup();
        };

        const handleMessageError = (e: MessageEvent) => {
          reject(new Error('Failed to deserialize message from worker'));
          cleanup();
        };

        const cleanup = () => {
          worker.removeEventListener('message', handleMessage);
          worker.removeEventListener('error', handleError);
          worker.removeEventListener('messageerror', handleMessageError);
        };

        worker.addEventListener('message', handleMessage);
        worker.addEventListener('error', handleError);
        worker.addEventListener('messageerror', handleMessageError);

        worker.postMessage(input);
      });
    },
    []
  );

  const getSampleAnalysisValues = useCallback(
    (sample?: Sample): ParameterLimitsValues => {
      return {
        a: sample?.analysis_values?.liao?.best_a || 0,
        b: sample?.analysis_values?.liao?.best_b || 0,
        pce: sample?.analysis_values?.liao?.pce || 0,
        largeDerivative: sample?.analysis_values?.liao?.large_derivative || 0
      };
    },
    []
  );

  const findBestValues = useCallback(async () => {
    if (!selectedInfo.sample?.uid) return;
    if (isCalculateLiaoAnalysisDisabled) return;

    const payload: CalculateSamplePayload = {
      sample_uid: selectedInfo.sample.uid,
      strategies_keys: ['liao'],
      input_data_map: {
        liao: {
          type: 'LIAO',
          bounds: [
            [0.000001, 1],
            [1, 1000]
          ],
          pce: customValues.pce > 0 ? customValues.pce : null,
          optimize_pce: customValues.pce <= 0
        }
      }
    };

    setLoading({ custom: false, bestValues: true });
    try {
      const response = await calculateSample(payload);

      const newWellSamplesData = data.wellSamples.map(sample => {
        if (sample.uid === response.uid) {
          return response;
        }

        return sample;
      });
      updateData({ wellSamples: newWellSamplesData });
      setSelectedInfo({ sample: response });
    } catch (error: any) {
      console.error('Error fetching samples:', error);
      const apiMessage = error?.response?.data?.message || error?.message;
      message.error(apiMessage || 'Something went wrong calculating the best values');
    } finally {
      setLoading({ custom: false, bestValues: false });
    }
  }, [
    selectedInfo.sample?.uid,
    customValues,
    data.wellSamples,
    setSelectedInfo,
    updateData,
    isCalculateLiaoAnalysisDisabled
  ]);

  const applyCustomLiao = useCallback(async () => {
    if (!selectedInfo.sample?.uid) return;
    if (isCalculateLiaoAnalysisDisabled) return;

    // Make sure that the values are numbers
    if (!isNaN(Number(customValues.a)) && !isNaN(Number(customValues.b))) {
      setLoading({ custom: true, bestValues: false });
      const liaoPayload: CalculateSamplePayload = {
        sample_uid: selectedInfo.sample.uid,
        strategies_keys: ['liao'],
        input_data_map: {
          liao: {
            type: 'LIAO',
            bounds: [
              [0.00001, 1],
              [1, 1000]
            ],
            custom_a: customValues.a,
            custom_b: customValues.b,
            pce: customValues.pce,
            optimize_pce: false,
            large_derivative: customValues.largeDerivative
          }
        }
      };
      try {
        const response = await calculateSample(liaoPayload);

        const newWellSamplesData = data.wellSamples.map(sample => {
          if (sample.uid === response.uid) {
            return response;
          }

          return sample;
        });
        updateData({ wellSamples: newWellSamplesData });
        setSelectedInfo({ sample: response });
      } catch (error: any) {
        console.error('Error fetching samples:', error);
        const apiMessage = error?.response?.data?.message || error?.message;
        message.error(apiMessage || 'Something went wrong applying the custom values');
      } finally {
        setLoading({ custom: false, bestValues: false });
      }
    }
  }, [
    selectedInfo.sample?.uid,
    customValues,
    data.wellSamples,
    setSelectedInfo,
    updateData,
    isCalculateLiaoAnalysisDisabled
  ]);

  const calculateLiao = useCallback(
    async (newCustomValues: Partial<ParameterLimitsValues>) => {
      // If the no sample is selected or the sample does not exist
      // in the wellSamples list, do nothing
      if (!selectedInfo.sample || !originalSelectedSample) return;

      // If the fields of ParameterLimits are disabled, do nothing
      if (isCalculateLiaoAnalysisDisabled) return;

      // Update the customValues with the new values
      const currentCustomValues = { ...customValues, ...newCustomValues };
      setCustomValues(currentCustomValues);

      if (!hasValidCustomLiaoValues(currentCustomValues)) return;

      const currentValues = getSampleAnalysisValues(selectedInfo.sample);
      const originalValues = getSampleAnalysisValues(originalSelectedSample);

      // Only recalculate if any of the data changed
      if (
        currentValues.a !== currentCustomValues.a ||
        currentValues.b !== currentCustomValues.b ||
        currentValues.pce !== currentCustomValues.pce ||
        currentValues.largeDerivative !== currentCustomValues.largeDerivative
      ) {
        try {
          const result = await calculateInWorker({
            input: {
              custom_a: currentCustomValues.a,
              custom_b: currentCustomValues.b,
              large_derivative: currentCustomValues.largeDerivative,
              pce: currentCustomValues.pce
            },
            sample: selectedInfo.sample
          });

          setSelectedInfo({ sample: result });

          return setIsLocalCalculated(true);
        } catch (error) {
          if (error instanceof Error) {
            message.error(error.message);
            return;
          }
          message.error('Error trying to calculate liao');
          return;
        }
      }

      // If the values are the same as the original, set the localCalculated as false
      if (
        originalValues.a === currentCustomValues.a &&
        originalValues.b === currentCustomValues.b &&
        originalValues.pce === currentCustomValues.pce &&
        originalValues.largeDerivative === currentCustomValues.largeDerivative
      ) {
        setIsLocalCalculated(false);
      }
    },
    [
      calculateInWorker,
      customValues,
      originalSelectedSample,
      selectedInfo.sample,
      setSelectedInfo,
      isCalculateLiaoAnalysisDisabled,
      getSampleAnalysisValues
    ]
  );

  const resetSample = useCallback(() => {
    setSelectedInfo({ sample: originalSelectedSample });
    setCustomValues(getSampleAnalysisValues(originalSelectedSample));
    setIsLocalCalculated(false);
  }, [originalSelectedSample, getSampleAnalysisValues, setSelectedInfo]);

  // Update the values if the selected sample uid changes (reflected by originalSelectedSample)
  useEffect(() => {
    setCustomValues(getSampleAnalysisValues(originalSelectedSample));
    setIsLocalCalculated(false);
  }, [originalSelectedSample, getSampleAnalysisValues]);

  // Run the worker on initial sample load so analysis_curves.liao (Pres/CorrectedSw)
  // is populated for the scatter points — the backend does not persist these arrays.
  // Depends on workerReady so it only fires after the worker is initialised (the worker
  // init effect sets workerReady=true, triggering a re-render that re-runs this effect).
  // No loop risk: after setSelectedInfo the originalSelectedSample memo returns the same
  // cached object (uid unchanged, wellSamples unchanged) so this effect does not re-fire.
  useEffect(() => {
    if (!workerReady || !originalSelectedSample) return;
    if (
      originalSelectedSample.analysis_curves?.liao?.PceMatch?.length ||
      originalSelectedSample.analysis_curves?.liao?.Pres?.length
    ) return;
    if (!originalSelectedSample.analysis_values?.liao) return;

    const values = getSampleAnalysisValues(originalSelectedSample);
    calculateInWorker({
      input: {
        custom_a: values.a,
        custom_b: values.b,
        large_derivative: values.largeDerivative,
        pce: values.pce
      },
      sample: originalSelectedSample
    })
      .then(result => setSelectedInfo(prev => {
        if (!prev.sample) return {};
        return {
          sample: {
            ...prev.sample,
            analysis_curves: {
              ...prev.sample.analysis_curves,
              liao: result.analysis_curves.liao
            }
          }
        };
      }))
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originalSelectedSample, workerReady]);

  // useEffect to get all the data used in the Liao Analysis tab
  useEffect(() => {
    if (!selectedInfo.sample) return;

    const analysisResults = selectedInfo.sample.analysis_curves.liao;
    const sampleResults = selectedInfo.sample.analysis_values.liao;
    const pceMatch = analysisResults?.PceMatch ?? analysisResults?.Pres ?? [];
    const correctedSw = analysisResults?.CorrectedSw ?? [];

    const original = pceMatch.map((pcValue, index) => {
      const swValue = correctedSw[index];
      if (pcValue === null || swValue === null) return null;
      return {
        x: swValue,
        y: pcValue
      };
    });

    const originalFiltered = original.filter(
      (value): value is { x: number; y: number } => value !== null
    );

    const calculated = sampleResults
      ? buildLiaoSampleCurve(sampleResults, numberOfPoints)
      : [];

    const lambdaValues = selectedInfo.sample.analysis_values.lambda_values;
    const pcMax = pceMatch
      .filter((value): value is number => typeof value === 'number' && Number.isFinite(value))
      .reduce((max, value) => Math.max(max, value), 0);
    const swirForLambda = selectedInfo.sample.input_sample_values?.swi;
    const lambdaCalculated = lambdaValues
      ? buildLambdaSampleCurve({ ...lambdaValues, pc_max: pcMax }, swirForLambda, numberOfPoints)
      : [];

    setChartData({
      calculated,
      original: originalFiltered,
      lambda: lambdaCalculated
    });
  }, [selectedInfo.sample, numberOfPoints]);

  // useEffect to set the worker ref
  useEffect(() => {
    workerRef.current = new Worker(
      new URL('./liao.worker.ts', import.meta.url),
      {
        type: 'module'
      }
    );
    setWorkerReady(true);

    return () => {
      workerRef.current?.terminate();
      setWorkerReady(false);
    };
  }, []);

  return {
    loading,
    chartData,
    isLocalCalculated,
    customValues,
    isCalculateLiaoAnalysisDisabled,
    numberOfPoints,
    setNumberOfPoints: (value: number) =>
      setNumberOfPoints(clampCurvePointCount(value, DEFAULT_SAMPLE_CURVE_POINTS)),
    resetSample,
    findBestValues,
    applyCustomLiao,
    calculateLiao
  };
}
